import React from 'react';
import { useRecoilValue } from 'recoil';
import { blockBenchmarkRelativeRawValue, blockBenchmarkType, blockMetrics, blockSettings } from 'venn-state';
import { StyledErrorState } from '../StyledErrorState';
import isNil from 'lodash/isNil';
import { useRequests } from '../../logic/useAnalysis';
import type { ErrorWrapper } from './errorWrapperTypes';
import { useDebugValue } from 'venn-utils';
import { CorrelationResultMap } from '../../../blocks/custom-blocks/correlation-block/correlation-content';
import { useFilteredRequestsAndAnalyses } from '../../../blocks/utils/hooks/use-filtered-requests';

export const CorrelationErrorWrapper: ErrorWrapper = ({ blockId, children }) => {
  const selectedMetricIds = useRecoilValue(blockMetrics(blockId));
  const blockType = useRecoilValue(blockSettings(blockId)).customBlockType;
  const requests = useRequests(blockId);
  const benchmarkType = useRecoilValue(blockBenchmarkType(blockId));
  const isRelativeToBenchmark = useRecoilValue(blockBenchmarkRelativeRawValue(blockId));
  const forceError = useDebugValue('debugBlockErrorState') === 'Correlation';
  const { analyses } = useFilteredRequestsAndAnalyses(blockId);

  const configureMetricsAndSubjectsMessage = 'Configure metrics and subjects in the block settings panel.';

  if (blockType === 'CORRELATION') {
    if (forceError || selectedMetricIds.length === 0) {
      return (
        <StyledErrorState
          selectedRefId={blockId}
          header="Select a metric and at least one subject"
          message={configureMetricsAndSubjectsMessage}
        />
      );
    }

    const metric = selectedMetricIds[0];
    // this check only applies to the error messages specific to correlation blocks not rolling correlation
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    const analysis = !isNil(metric) ? analyses[CorrelationResultMap[metric]]?.[0] : undefined;
    if (analysis?.message) {
      return (
        <StyledErrorState
          selectedRefId={blockId}
          header="No analysis available"
          message={analysis.message.text || 'An error occurred while running the analysis.'}
        />
      );
    }

    const maybeSubject = requests[0]?.subject;
    const subjectIsFund = maybeSubject?.subjectType === 'INVESTMENT' || !isNil(maybeSubject?.strategy?.fund);
    if (metric === 'portfolioCorrelation' && subjectIsFund) {
      return (
        <StyledErrorState
          selectedRefId={blockId}
          header="Portfolio Correlation is only available for portfolios"
          message="Please make sure a portfolio is the top subject selected"
        />
      );
    }

    if (benchmarkType === 'INDIVIDUAL' && isRelativeToBenchmark) {
      return (
        <StyledErrorState
          selectedRefId={blockId}
          header="Unable to run analysis"
          message="Please use common benchmark or disable relative to benchmark setting"
        />
      );
    }
  }

  return children;
};
